/**
 * MY Contact 디테일 — addressData / contactInfoData 구성 (ContactDetailPage 와 동일 규칙)
 * Register Director 등 Global Contact 주소 UI 재사용용
 */
import { extractAddressArrayFromProfileJsonColumn } from './profileServiceBusinessColumn';

export type MyContactDetailFormState = {
    addressData: Record<string, unknown>;
    contactInfoData: Record<string, unknown>;
    useBranchAddresses: boolean;
    branchResidentialList: unknown[];
    branchResidentialShowPool: unknown[];
    branchServiceShowPool: unknown[];
    branchBusinessShowPool: unknown[];
    isCorporate: boolean;
    isIndividual: boolean;
    showResidentialMultiHint: boolean;
    showServiceMultiHint: boolean;
    showBusinessMultiHint: boolean;
};

function isPrimaryAddressDisplayFlag(value: unknown): boolean {
    return value === 1 || value === '1' || value === true;
}

function isAddressMeaningful(a: unknown): boolean {
    if (!a || typeof a !== 'object') return false;
    const row = a as Record<string, unknown>;
    const fields = [
        row.street1, row.street01, row.address1, row.addressLine1,
        row.street2, row.street02, row.address2, row.addressLine2,
        row.street3, row.street03, row.address3, row.addressLine3,
        row.postcode, row.post_code, row.zip,
        row.city, row.state, row.country,
    ];
    return fields.some((v) => v != null && String(v).trim() !== '');
}

function pickPrimaryResidentialForDisplay(addr: unknown): Record<string, unknown> | null {
    if (addr == null) return null;
    if (Array.isArray(addr)) {
        if (addr.length === 0) return null;
        const primary = addr.find((item) =>
            isPrimaryAddressDisplayFlag(
                (item as Record<string, unknown>)?.isPrimaryAddress
                    ?? (item as Record<string, unknown>)?.is_primary_address,
            ),
        );
        if (primary && typeof primary === 'object') return { ...(primary as object) };
        const meaningful = addr.find((item) => isAddressMeaningful(item));
        if (meaningful && typeof meaningful === 'object') return { ...(meaningful as object) };
        const first = addr[0];
        return first && typeof first === 'object' ? { ...(first as object) } : null;
    }
    if (typeof addr === 'object') {
        const row = addr as Record<string, unknown>;
        const flag = row.isPrimaryAddress ?? row.is_primary_address;
        if (isPrimaryAddressDisplayFlag(flag)) return row;
        if (isAddressMeaningful(row)) return row;
        return null;
    }
    return null;
}

function getResidentialPrimaryFromResidentialAddrJsonDataColumn(contact: Record<string, unknown>): Record<string, unknown> | null {
    const raw = contact.RESIDENTIAL_ADDR_JSON_DATA ?? contact.residential_addr_json_data;
    if (raw == null || raw === '') return null;
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!parsed || typeof parsed !== 'object') return null;
        const obj = parsed as Record<string, unknown>;
        if (obj.residential_address != null) {
            const inner = pickPrimaryResidentialForDisplay(obj.residential_address);
            if (inner) return inner;
        }
        const fromRoot = pickPrimaryResidentialForDisplay(parsed);
        if (fromRoot) return fromRoot;
        const hasLine =
            String(obj.addressLine1 ?? obj.street01 ?? obj.address1 ?? '').trim() !== ''
            || String(obj.addressLine2 ?? obj.street02 ?? '').trim() !== ''
            || String(obj.addressLine3 ?? obj.street03 ?? '').trim() !== ''
            || String(obj.country ?? '').trim() !== ''
            || String(obj.postcode ?? obj.post_code ?? '').trim() !== '';
        if (hasLine) return obj;
    } catch {
        return null;
    }
    return null;
}

function pickResidentialForMyAddressForm(contact: Record<string, unknown>): Record<string, unknown> | null {
    if (contact.USE_BRANCH_ADDRESSES) {
        const ra = contact.residential_address;
        if (Array.isArray(ra) && ra.length > 0) {
            return pickPrimaryResidentialForDisplay(ra) || (typeof ra[0] === 'object' ? { ...ra[0] } : null);
        }
        return null;
    }
    const fromArray = pickPrimaryResidentialForDisplay(contact.residential_address);
    if (fromArray) return fromArray;
    const regFromApi = contact.registeredAddress;
    if (regFromApi && typeof regFromApi === 'object' && isAddressMeaningful(regFromApi)) {
        return { ...(regFromApi as object) };
    }
    return getResidentialPrimaryFromResidentialAddrJsonDataColumn(contact);
}

function myProfileAddrListPrimaryOrAll(
    raw: unknown,
    key: 'service_address' | 'business_address',
): Record<string, unknown>[] {
    const arr = extractAddressArrayFromProfileJsonColumn(raw, key);
    if (arr.length === 0) return [];
    const prim = arr.filter((item) =>
        isPrimaryAddressDisplayFlag(item?.isPrimaryAddress ?? item?.is_primary_address),
    );
    const list = prim.length > 0 ? prim : arr;
    return list.map((row) => ({ ...row }));
}

function createBranchAddressDataShell(contact: Record<string, unknown>) {
    return {
        addressType: 'B07001' as const,
        CONTACT_REGISTER_TYPE: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
        contactRegisterType: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
        localAddress: {
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            country: '',
            state: '',
            city: '',
            postcode: '',
            post_code: '',
        },
        foreignAddress: {
            addressLine1: '',
            addressLine2: '',
        },
    };
}

function normalizeResidentialBranchListFromContact(contact: Record<string, unknown>): Record<string, unknown>[] {
    const ra = contact.residential_address ?? contact.residentialAddress;
    if (Array.isArray(ra)) return ra.map((x) => ({ ...(x as object) }));
    if (ra && typeof ra === 'object') return [{ ...(ra as object) }];
    return [];
}

/** MY Corporate — USE_BRANCH_ADDRESSES 플래그 없어도 branch 배열이 있으면 다건 UI */
function shouldUseBranchAddressesForHydrate(contact: Record<string, unknown>, isCorporate: boolean): boolean {
    if (contact.USE_BRANCH_ADDRESSES === true || contact.USE_BRANCH_ADDRESSES === 1) return true;
    if (!isCorporate) return false;
    if (normalizeResidentialBranchListFromContact(contact).length > 0) return true;
    const pool = contact.branch_address_show_pool ?? contact.BRANCH_ADDRESS_SHOW_POOL;
    const poolRes =
        pool && typeof pool === 'object' && Array.isArray((pool as { residential?: unknown[] }).residential)
            ? (pool as { residential: unknown[] }).residential
            : [];
    if (poolRes.length > 0) return true;
    const svc = contact.service_address;
    const biz = contact.business_address;
    if (Array.isArray(svc) && svc.length > 0) return true;
    if (Array.isArray(biz) && biz.length > 0) return true;
    return false;
}

function branchAddressListFromContact(
    contact: Record<string, unknown>,
    key: 'service_address' | 'business_address',
): Record<string, unknown>[] {
    const top = contact[key];
    if (Array.isArray(top)) return top.map((x) => ({ ...x }));
    try {
        const raw = contact.CONTACT_JSON_DATA ?? contact.contact_json_data;
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw && typeof raw === 'object' ? raw : {};
        const legacy = (parsed as Record<string, unknown>)?.[key];
        if (Array.isArray(legacy)) return legacy.map((x) => ({ ...(x as object) }));
    } catch {
        /* ignore */
    }
    return [];
}

function buildEmailsForDisplay(contact: Record<string, unknown>): string[] {
    const contactEmailsRaw = contact.CONTACT_EMAILS;
    const emailList = Array.isArray(contactEmailsRaw) ? contactEmailsRaw : [];
    const primaryEmail = contact.CONTACT_EMAIL ? String(contact.CONTACT_EMAIL).trim() : '';
    if (primaryEmail) {
        return [primaryEmail, ...emailList]
            .map((email) => String(email ?? '').trim())
            .filter((email, idx, arr) => email.length > 0 && arr.indexOf(email) === idx)
            .slice(0, 2);
    }
    if (emailList.length > 0) {
        return emailList
            .map((email) => String(email ?? '').trim())
            .filter((email) => email.length > 0)
            .slice(0, 2);
    }
    return [''];
}

/**
 * @param contact GET /Contact/:id 응답 contact 객체
 * @param entityCd Register/Entity Officer 컨텍스트 (branch show pool 필터)
 */
export function hydrateMyContactDetailForms(
    contact: Record<string, unknown>,
    entityCd = '',
): MyContactDetailFormState {
    const trimmedEntity = String(entityCd || '').trim();
    const category = String(contact.CATEGORY_CD || contact.category_cd || '').trim().toUpperCase();
    const isCorporate = category === 'B09001';
    const isIndividual = !isCorporate;
    const useBranchHydrate = shouldUseBranchAddressesForHydrate(contact, isCorporate);

    let addressData: Record<string, unknown> = createBranchAddressDataShell(contact);
    const registeredAddr = pickResidentialForMyAddressForm(contact);

    if (!useBranchHydrate && registeredAddr) {
        const regFromApi = contact.registeredAddress as Record<string, unknown> | undefined;
        const merged = registeredAddr && regFromApi && typeof regFromApi === 'object'
            ? {
                ...registeredAddr,
                state: String(registeredAddr.state || regFromApi.state || '').trim(),
                city: String(registeredAddr.city || regFromApi.city || '').trim(),
                country: String(registeredAddr.country || regFromApi.country || '').trim(),
                stateCode: regFromApi.stateCode || registeredAddr.stateCode,
                cityCode: regFromApi.cityCode || registeredAddr.cityCode,
                postcodeCode: regFromApi.postcodeCode || registeredAddr.postcodeCode,
                countryCode: regFromApi.countryCode || registeredAddr.countryCode,
                postcode:
                    registeredAddr.postcode
                    || registeredAddr.post_code
                    || regFromApi.postcode
                    || regFromApi.postalCode,
                post_code:
                    registeredAddr.post_code
                    || registeredAddr.postcode
                    || regFromApi.post_code
                    || regFromApi.postcode,
            }
            : registeredAddr;

        addressData = {
            addressType: 'B07001',
            CONTACT_REGISTER_TYPE: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
            contactRegisterType: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
            localAddress: {
                addressLine1: merged.addressLine1 || merged.street01 || '',
                addressLine2: merged.addressLine2 || merged.street02 || '',
                addressLine3: merged.addressLine3 || merged.street03 || '',
                country: merged.country || merged.countryCode || '',
                state: merged.state || '',
                city: merged.city || '',
                postcode: merged.postcode || merged.post_code || merged.postalCode || '',
                post_code: merged.post_code || merged.postcode || merged.postalCode || '',
                countryCode: merged.countryCode || merged.country || '',
                stateCode: merged.stateCode || '',
                cityCode: merged.cityCode || '',
                postcodeCode: merged.postcodeCode || '',
                IsSync: merged.IsSync ?? merged.isSync,
            },
            foreignAddress: { addressLine1: '', addressLine2: '' },
        };
    }

    const emailsForDisplay = buildEmailsForDisplay(contact);
    const contactInfoData: Record<string, unknown> = {
        countryCode: contact.COUNTRY_CD || '',
        areaCode: contact.AREA_CODE || '',
        mobileNos: contact.MOBILE_NO ? [contact.MOBILE_NO] : [''],
        emails: emailsForDisplay,
        emailMainYn: emailsForDisplay.map(() => true).slice(0, 2),
        emailSyncYn: emailsForDisplay.map(() => true).slice(0, 2),
        note: contact.NOTE || '',
        CONTACT_EMAIL: contact.CONTACT_EMAIL ? String(contact.CONTACT_EMAIL).trim() : '',
        CONTACT_REGISTER_TYPE: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
        contactRegisterType: contact.CONTACT_REGISTER_TYPE || contact.contactRegisterType || '',
        ADDRESS_TYPE_CD: contact.ADDRESS_TYPE_CD || 'B07001',
        addressTypeCd: contact.ADDRESS_TYPE_CD || 'B07001',
        SERVICE_ADDR_JSON_DATA: contact.SERVICE_ADDR_JSON_DATA ?? contact.service_addr_json_data ?? null,
        BUSINESS_ADDR_JSON_DATA: contact.BUSINESS_ADDR_JSON_DATA ?? contact.business_addr_json_data ?? null,
        USE_BRANCH_ADDRESSES: useBranchHydrate,
        serviceAddressList: [] as unknown[],
        businessAddressList: [] as unknown[],
    };

    if (useBranchHydrate) {
        const branchResidential = contact.residential_address;
        if (Array.isArray(branchResidential)) {
            contactInfoData.residential_address =
                branchResidential.length > 0 ? branchResidential.map((x) => ({ ...x })) : [];
        } else if (branchResidential && typeof branchResidential === 'object') {
            contactInfoData.residential_address = [{ ...(branchResidential as object) }];
        } else {
            contactInfoData.residential_address = [];
        }
        contactInfoData.serviceAddressList = branchAddressListFromContact(contact, 'service_address');
        contactInfoData.businessAddressList = branchAddressListFromContact(contact, 'business_address');
        contactInfoData.CONTACT_JSON_DATA = {
            service_address: contactInfoData.serviceAddressList,
            business_address: contactInfoData.businessAddressList,
        };
    } else {
        if (registeredAddr) {
            contactInfoData.residential_address = registeredAddr;
        }
        const svcRaw = contact.SERVICE_ADDR_JSON_DATA ?? contact.service_addr_json_data ?? null;
        const bizRaw = contact.BUSINESS_ADDR_JSON_DATA ?? contact.business_addr_json_data ?? null;
        contactInfoData.serviceAddressList = myProfileAddrListPrimaryOrAll(svcRaw, 'service_address');
        contactInfoData.businessAddressList = myProfileAddrListPrimaryOrAll(bizRaw, 'business_address');

        const serviceList = contactInfoData.serviceAddressList as Record<string, unknown>[];
        let serviceSource: Record<string, unknown> | null = null;
        if (serviceList.length > 0) {
            const primaryService = serviceList.find((addr) =>
                isPrimaryAddressDisplayFlag(addr?.isPrimaryAddress ?? addr?.is_primary_address),
            );
            serviceSource = primaryService ? { ...primaryService } : { ...serviceList[0] };
        }
        contactInfoData.altLocalAddress = serviceSource
            ? {
                addressLine1: serviceSource.addressLine1 || serviceSource.street01 || '',
                addressLine2: serviceSource.addressLine2 || serviceSource.street02 || '',
                addressLine3: serviceSource.addressLine3 || serviceSource.street03 || '',
                country: serviceSource.country || '',
                state: serviceSource.state || '',
                city: serviceSource.city || '',
                postcode: serviceSource.postcode || serviceSource.post_code || serviceSource.postalCode || '',
                post_code: serviceSource.post_code || serviceSource.postcode || serviceSource.postalCode || '',
                countryCode: serviceSource.countryCode || '',
                stateCode: serviceSource.stateCode || '',
                cityCode: serviceSource.cityCode || '',
                postcodeCode: serviceSource.postcodeCode || '',
                IsSync: serviceSource.IsSync ?? serviceSource.isSync,
            }
            : {
                addressLine1: '',
                addressLine2: '',
                addressLine3: '',
                country: '',
                state: '',
                city: '',
                postcode: '',
                post_code: '',
                countryCode: '',
                stateCode: '',
                cityCode: '',
                postcodeCode: '',
            };
    }

    const branchPool = (contact.branch_address_show_pool ?? contact.BRANCH_ADDRESS_SHOW_POOL) as
        | { residential?: unknown[]; service?: unknown[]; business?: unknown[] }
        | null
        | undefined;

    const branchResidentialEntityList = (() => {
        if (!useBranchHydrate) return [] as Record<string, unknown>[];
        const ra = contactInfoData.residential_address ?? contact.residential_address;
        if (Array.isArray(ra)) return ra.map((x) => ({ ...(x as object) }));
        if (ra && typeof ra === 'object') return [{ ...(ra as object) }];
        return [];
    })();

    /** Entity Officer: entity MAP 슬롯 + contact 전체 Registered 풀 중 더 많은 쪽 표시 (다건 Registered) */
    const branchResidentialList = (() => {
        if (!useBranchHydrate) return [];
        const poolRes =
            branchPool && Array.isArray(branchPool.residential)
                ? (branchPool.residential as Record<string, unknown>[]).map((x) => ({ ...x }))
                : [];
        if (trimmedEntity && poolRes.length > branchResidentialEntityList.length) {
            return poolRes;
        }
        return branchResidentialEntityList;
    })();

    const branchServiceList = Array.isArray(contactInfoData.serviceAddressList)
        ? contactInfoData.serviceAddressList
        : [];
    const branchBusinessList = Array.isArray(contactInfoData.businessAddressList)
        ? contactInfoData.businessAddressList
        : [];

    const branchResidentialShowPool =
        trimmedEntity && branchPool?.residential ? branchPool.residential : branchResidentialList;
    const branchServiceShowPool =
        trimmedEntity && branchPool?.service ? branchPool.service : branchServiceList;
    const branchBusinessShowPool =
        trimmedEntity && branchPool?.business ? branchPool.business : branchBusinessList;

    let showResidentialMultiHint = false;
    let showServiceMultiHint = false;
    let showBusinessMultiHint = false;
    if (useBranchHydrate) {
        const resCount = branchResidentialList.length;
        const svcCount = branchServiceList.length;
        const busCount = branchBusinessList.length;
        showResidentialMultiHint = resCount > 1;
        showServiceMultiHint = svcCount > 1;
        showBusinessMultiHint = busCount > 1;
    } else if (!useBranchHydrate && isIndividual) {
        const svcRaw = contact.SERVICE_ADDR_JSON_DATA ?? contact.service_addr_json_data;
        const bizRaw = contact.BUSINESS_ADDR_JSON_DATA ?? contact.business_addr_json_data;
        showResidentialMultiHint = false;
        showServiceMultiHint = myProfileAddrListPrimaryOrAll(svcRaw, 'service_address').length > 1;
        showBusinessMultiHint = myProfileAddrListPrimaryOrAll(bizRaw, 'business_address').length > 1;
    }

    return {
        addressData,
        contactInfoData,
        useBranchAddresses: useBranchHydrate,
        branchResidentialList,
        branchResidentialShowPool: Array.isArray(branchResidentialShowPool) ? branchResidentialShowPool : [],
        branchServiceShowPool: Array.isArray(branchServiceShowPool) ? branchServiceShowPool : [],
        branchBusinessShowPool: Array.isArray(branchBusinessShowPool) ? branchBusinessShowPool : [],
        isCorporate,
        isIndividual,
        showResidentialMultiHint,
        showServiceMultiHint,
        showBusinessMultiHint,
    };
}
